import { InvestorTermsModal } from '@dealstack/frontend/shared/components/TermsModals/InvestorTermsModal';
import { PrivacyPolicyModal } from '@dealstack/frontend/shared/components/TermsModals/PrivacyPolicyModal';
import { useTranslationAuth } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { Flex } from '@mantine/core';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import { useEffect, useState } from 'react';
import {
	getUser,
	updateInvestorPhone,
} from '@dealstack/frontend/shared/dataExchange/services/auth.service';
import { useNavigate } from 'react-router-dom';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { redirectToLoginPage } from '@dealstack/frontend/shared/redirecting';
import { ApplicationType } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useMutation, useQuery } from '@tanstack/react-query';
import { needsInvestorPhone } from '@dealstack/frontend/shared/pages/InvestorPhone/needsInvestorPhone';
import {
	InvestorPhoneForm,
	InvestorPhoneSubmission,
} from '@dealstack/frontend/shared/pages/InvestorPhone/components/InvestorPhoneForm';

interface Props {
	applicationType: ApplicationType;
	onSuccessRedirect: string;
}

export const InvestorPhone = ({ applicationType, onSuccessRedirect }: Props) => {
	const [isPrivacyModalOpened, setIsPrivacyModalOpened] = useState(false);
	const [isTermsModalOpened, setIsTermsModalOpened] = useState(false);
	const tAuth = useTranslationAuth();

	const navigate = useNavigate();

	const session = useQuery({
		queryKey: [API_PATHS.USER_GET],
		queryFn: getUser,
		retry: false,
		meta: { suppressErrorToast: true },
	});

	const user = session.data?.payload;
	const isPhoneStepNeeded = user !== undefined && needsInvestorPhone(user, applicationType);

	useEffect(() => {
		if (session.isError) {
			redirectToLoginPage();
		}
	}, [session.isError]);

	useEffect(() => {
		if (user !== undefined && !isPhoneStepNeeded) {
			navigate(onSuccessRedirect);
		}
	}, [user, isPhoneStepNeeded]);

	const updatePhoneRequest = useMutation({
		mutationFn: updateInvestorPhone,
	});

	const onSubmit = (submission: InvestorPhoneSubmission) => {
		updatePhoneRequest.mutate(submission, {
			onSuccess: () => navigate(onSuccessRedirect),
			onError: () =>
				showToastV2({
					title: tAuth('register.toast.generic_error.message'),
					state: 'error',
				}),
		});
	};

	if (!isPhoneStepNeeded) {
		return <FullscreenLoader name="pageLoading" />;
	}

	return (
		<Flex
			direction="column"
			p="2rem"
			gap="1rem"
			w="470px"
			bg="#F4F5F6"
			sx={{
				boxSizing: 'border-box',
				itemsCenter: 'items-center',
				borderRadius: '0.5rem',
				border: '1px solid #E9EBED',
			}}
		>
			<InvestorPhoneForm
				onSubmit={onSubmit}
				isSubmitting={updatePhoneRequest.isPending}
				setIsTermsModalOpened={setIsTermsModalOpened}
				setIsPrivacyModalOpened={setIsPrivacyModalOpened}
			/>
			{isPrivacyModalOpened && (
				<PrivacyPolicyModal onClose={() => setIsPrivacyModalOpened(false)} />
			)}
			{isTermsModalOpened && (
				<InvestorTermsModal onClose={() => setIsTermsModalOpened(false)} />
			)}
		</Flex>
	);
};
